import { Backdrop, Box, Grid, makeStyles, Typography } from "@material-ui/core";
import DialogConfirmChangeOrder from "@saleor/changeOrderExport/components/DialogConfirmChangeOrder";
import DialogConfirmDelete from "@saleor/components/DialogConfirmDelete";
import MyTypography from "@saleor/components/MyTypography";
import SnackbarAlert from "@saleor/components/SnackbarAlert";
import {
  ScgpUserQuery,
  useScgpDeleteUserMutation,
  useScgpUserRegisterMutation,
  useScgpUserUpdateMutation
} from "@saleor/graphql";
import useNavigator from "@saleor/hooks/useNavigator";
import {
  FormType,
  ParentGroupCode,
  UserDetailMode
} from "@saleor/userManagement/constants";
import {
  userDetailUrl,
  userManagementSection
} from "@saleor/userManagement/urls";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import FormCustomer from "./FormCustomer";
import FormOther from "./FormOther";
import FormOtherAgency from "./FormOtherAgency";
import FormSale from "./FormSale";
import { getFormInputData, getValidationSchema } from "./utils";

const useStyles = makeStyles(
  theme => ({
    userDetailWrap: {
      "& .MuiSelect-iconOutlined": {
        backgroundColor: "#ffffff",
        padding: "11px 5px 7px 5px",
        right: "0px",
        top: "1px"
      }
    },
    root: {
      borderRadius: 6,
      background: "white",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      overflow: "hidden",
      marginTop: "1.6rem"
    },
    header: {
      padding: "1rem 1.6rem",
      background: theme.palette.gardient.medium,
      colors: "white",
      display: "flex",
      alignItems: "center"
    },
    mainContent: {
      background: "#FFF",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      borderRadius: 12,
      padding: "2.4rem",
      "& .MuiGrid-root": {
        marginTop: 0
      },
      "& .MuiGrid-item": {
        paddingTop: 0
      }
    },
    titleCustomerName: {
      marginBottom: 16
    },
    title: {
      color: "white",
      marginRight: "4px"
    },
    titleDelete: {
      color: theme.palette.error.light
    },
    subtitle: {
      textTransform: "uppercase",
      color: theme.palette.info.main
    },
    errorMessage: {
      color: theme.palette.error.light,
      lineHeight: "18px",
      marginTop: 4
    },
    snackHeadTitle: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      columnGap: "1.2rem"
    },
    backDrop: {
      zIndex: 1
    }
  }),
  { name: "UserDetailForm" }
);

enum ModalMode {
  ADD_CONFIRMATION = "ADD_CONFIRMATION",
  ADD_SUCCESS = "ADD_SUCCESS",
  EDIT_CONFIRMATION = "EDIT_CONFIRMATION",
  EDIT_SUCCESS = "EDIT_SUCCESS"
}

export interface UserDetailFormProps {
  user: ScgpUserQuery;
  submitCall: number;
  mode: UserDetailMode;
  setMode: (mode: UserDetailMode) => void;
  refetchUser: () => void;
  groupId: string;
  groupCode: string;
  formType: number;
}

interface Error {
  open: boolean;
  message: string;
}

const UserDetailForm: React.FC<UserDetailFormProps> = ({
  user,
  submitCall,
  mode,
  setMode,
  groupCode,
  groupId,
  refetchUser,
  formType
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const navigate = useNavigator();
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [openError, setOpenError] = useState<Error>({
    open: false,
    message: ""
  });
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const formik = useFormik({
    initialValues: {
      isScgp: false,
      firstName: user?.scgpUser?.firstName,
      lastName: user?.scgpUser?.lastName,
      employeeId: user?.scgpUser?.extendData?.employeeId,
      displayName: user?.scgpUser?.extendData?.displayName,
      userRoles: user?.scgpUser?.permissionGroups?.map(x => x.id) || [],
      customerType: user?.scgpUser?.extendData?.customerType || "Internal",
      adUser: user?.scgpUser?.extendData?.adUser,
      email: user?.scgpUser?.email,
      companyEmail: user?.scgpUser?.extendData?.companyEmail,
      soldTo: user?.scgpUser?.soldTos?.map(x => x.id) || [],
      isActive: user?.scgpUser?.isActive,
      businessUnits: user?.scgpUser?.extendData?.scgpBus?.map(x => x.id) || [],
      saleOrganizations:
        user?.scgpUser?.extendData?.scgpSalesOrganizations?.map(x => x.id) ||
        [],
      saleGroups:
        user?.scgpUser?.extendData?.scgpSalesGroups?.map(x => x.id) || [],
      distributionChannels:
        user?.scgpUser?.extendData?.scgpDistributionChannels?.map(x => x.id) ||
        [],
      divisions:
        user?.scgpUser?.extendData?.scgpDivisions?.map(x => x.id) || [],
      saleOffices:
        user?.scgpUser?.extendData?.scgpSalesOffices?.map(x => x.id) || [],
      saleId: user?.scgpUser?.extendData?.saleId,
      sapId: user?.scgpUser?.extendData?.sapId
    },
    onSubmit: data => {
      if (mode === UserDetailMode.EDIT) {
        if (modalMode !== ModalMode.EDIT_CONFIRMATION) {
          setModalMode(ModalMode.EDIT_CONFIRMATION);
        } else {
          setLoading(true);
          updateUser({
            variables: {
              id: user?.scgpUser.id,
              input: {
                ...getFormInputData(
                  mode,
                  formType,
                  groupId,
                  user,
                  data,
                  groupCode
                ),
                isActive: data.isActive
              }
            }
          });
        }
      } else {
        if (modalMode !== ModalMode.ADD_CONFIRMATION) {
          setModalMode(ModalMode.ADD_CONFIRMATION);
        } else {
          setLoading(true);
          registerUser({
            variables: {
              input: getFormInputData(
                mode,
                formType,
                groupId,
                user,
                data,
                groupCode
              )
            }
          });
        }
      }
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: () =>
      Yup.lazy(_ => getValidationSchema(mode, formType, groupCode))
  });

  const [updateUser] = useScgpUserUpdateMutation({
    onCompleted: data => {
      setLoading(false);
      const errors = data?.scgpUserUpdate?.errors;
      if (errors.length > 0) {
        errors.forEach(err => {
          formik.setFieldError(err.field, err.message);
        });
        setModalMode(null);
      } else {
        setMode(UserDetailMode.VIEW);
        refetchUser();
        setModalMode(ModalMode.EDIT_SUCCESS);
      }
    }
  });

  const [registerUser] = useScgpUserRegisterMutation({
    onCompleted: data => {
      setLoading(false);
      const errors = data?.scgpUserRegister?.errors;
      if (errors.length > 0) {
        errors.forEach(err => {
          if (err.field === "adUser") {
            setOpenError({
              open: true,
              message: err.message
            });
          } else {
            formik.setFieldError(err.field, err.message);
          }
        });
        setModalMode(null);
      } else {
        const id = data.scgpUserRegister.user.id;
        navigate(userDetailUrl(id));
        setMode(UserDetailMode.VIEW);
        setLoading(false);
        setModalMode(ModalMode.ADD_SUCCESS);
        formik.setFieldValue("isActive", true);
      }
    }
  });

  useEffect(() => {
    if (submitCall) {
      formik.handleSubmit();
    }
  }, [submitCall]);

  const [deleteUser] = useScgpDeleteUserMutation({
    onCompleted: () => {
      setOpenDelete(false);
      navigate(userManagementSection);
    },
    onError: () => {
      setOpenDelete(false);
    }
  });

  const handleOnKeyPress: React.KeyboardEventHandler<HTMLDivElement> = e => {
    if (!new RegExp("^[A-Za-z0-9]*$").test(e.key)) {
      e.preventDefault();
    }
  };

  const handleCopyPaste = e => {
    e.preventDefault();
  };

  const handleDeleteUser = () => {
    if (!user?.scgpUser?.id) {
      return;
    }
    deleteUser({
      variables: {
        user_id: user?.scgpUser?.id
      }
    });
  };

  return (
    <>
      <div className={classes.userDetailWrap}>
        <Box className={classes.root}>
          <div className={classes.header}>
            <Typography variant="h6" className={classes.title}>
              {intl.formatMessage({
                defaultMessage: "ข้อมูลผู้ใช้งาน"
              })}
            </Typography>
          </div>
          <div className={classes.mainContent}>
            {mode === UserDetailMode.VIEW && (
              <Grid
                container
                item
                md={12}
                className={classes.titleCustomerName}
                data-testid="titleCustomerName"
              >
                <MyTypography variant="subtitle2" className={classes.subtitle}>
                  {`${user?.scgpUser?.firstName} ${user?.scgpUser?.lastName}`}
                </MyTypography>
              </Grid>
            )}
            <Grid container spacing={6} item md={11}>
              {formType === FormType.OTHER && (
                <FormOther
                  handleOpenDelete={() => setOpenDelete(true)}
                  handleOnKeyPress={handleOnKeyPress}
                  handleOnPaste={handleCopyPaste}
                  formik={formik}
                  user={user}
                  mode={mode}
                  groupId={groupId}
                />
              )}
              {formType === FormType.SALES && (
                <FormSale
                  handleOpenDelete={() => setOpenDelete(true)}
                  handleOnKeyPress={handleOnKeyPress}
                  handleOnPaste={handleCopyPaste}
                  formik={formik}
                  user={user}
                  mode={mode}
                  groupId={groupId}
                />
              )}
              {formType === FormType.CUSTOMER && (
                <FormCustomer
                  handleOpenDelete={() => setOpenDelete(true)}
                  handleOnKeyPress={handleOnKeyPress}
                  handleOnPaste={handleCopyPaste}
                  formik={formik}
                  user={user}
                  mode={mode}
                  groupId={groupId}
                />
              )}
            </Grid>
          </div>
        </Box>
        {formType === FormType.OTHER && groupCode !== ParentGroupCode.SECTION && (
          <Box className={classes.root}>
            <div className={classes.header}>
              <Typography variant="h6" className={classes.title}>
                {intl.formatMessage({
                  defaultMessage: "ข้อมูลหน่วยงาน"
                })}
              </Typography>
            </div>
            <div className={classes.mainContent}>
              <Grid container spacing={6} item md={11}>
                <FormOtherAgency
                  formik={formik}
                  user={user}
                  mode={mode}
                  groupId={groupId}
                />
              </Grid>
            </div>
          </Box>
        )}
        <SnackbarAlert
          severity={"error"}
          open={openError.open}
          onClose={() =>
            setOpenError({ open: false, message: openError.message })
          }
          anchorOrigin={{ horizontal: "center", vertical: "top" }}
        >
          <div className={classes.snackHeadTitle}>
            <MyTypography variant="body3" style={{ color: "#ffffff" }}>
              {openError.message}
            </MyTypography>
          </div>
        </SnackbarAlert>
        <SnackbarAlert
          severity={"success"}
          open={
            modalMode === ModalMode.ADD_SUCCESS ||
            modalMode === ModalMode.EDIT_SUCCESS
          }
          onClose={() => setModalMode(null)}
          anchorOrigin={{ horizontal: "center", vertical: "top" }}
        >
          <div className={classes.snackHeadTitle}>
            <MyTypography
              variant="subtitle2"
              style={{ color: "#ffffff", marginRight: "2px" }}
            >
              {modalMode === ModalMode.ADD_SUCCESS
                ? "Add user successfully"
                : "Changes saved"}
            </MyTypography>
            <MyTypography variant="body3" style={{ color: "#ffffff" }}>
              {modalMode === ModalMode.ADD_SUCCESS
                ? "เพิ่มผู้ใช้งานสำเร็จ"
                : "บันทึกข้อมูลผู้ใช้งานสำเร็จ"}
            </MyTypography>
          </div>
        </SnackbarAlert>
        <DialogConfirmChangeOrder
          testId="confirmSaveModal"
          onClose={() => setModalMode(null)}
          open={
            modalMode === ModalMode.ADD_CONFIRMATION ||
            modalMode === ModalMode.EDIT_CONFIRMATION
          }
          onOk={formik.handleSubmit}
          title={
            modalMode === ModalMode.ADD_CONFIRMATION
              ? "ยืนยันเพิ่มผู้ใช้งาน"
              : "บันทึกข้อมูลผู้ใช้งาน"
          }
          content={
            modalMode === ModalMode.ADD_CONFIRMATION
              ? "กรุณากด “ยืนยัน” เพื่อเพิ่มผู้ใช้งานหรือกด “ยกเลิก” เพื่อกลับไป"
              : "กรุณากด “ยืนยัน” เพื่อบันทึกข้อมูลผู้ใช้งาน หรือกด “ยกเลิก” เพื่อกลับไป"
          }
          loading={loading}
        />
        <DialogConfirmDelete
          testId="confirmDeleteModal"
          onConfirm={handleDeleteUser}
          onCancel={() => setOpenDelete(false)}
          open={openDelete}
          title={
            <span className={classes.titleDelete}>
              คุณต้องการลบ Account นี้หรือไม่
            </span>
          }
          cancelText={"กลับไป"}
          okText={"ยืนยัน"}
          content={
            <MyTypography variant={"body2"}>
              {intl.formatMessage({
                defaultMessage:
                  'กรุณากด "ยืนยัน" เพื่อลบ Account ที่เลือกหรือกด "กลับไป" เพื่อปิดหน้าต่างนี้ี้'
              })}
            </MyTypography>
          }
        />
        <Backdrop
          open={
            modalMode === ModalMode.ADD_CONFIRMATION ||
            modalMode === ModalMode.EDIT_CONFIRMATION
          }
          className={classes.backDrop}
        />
      </div>
    </>
  );
};

export default UserDetailForm;
